import { RouteIcon } from 'lucide-react';
import { useMemo } from 'react';

import type { ReturnDTO, ReturnLineDTO } from '@invenco/supply-interface';

import { DetailGroup } from '@/components/detail-group';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { useEntityDetailsQuery } from '@/shared/hooks/queries';
import { useRequiredParam } from '@/shared/hooks/use-required-param';
import { returnStatusType } from '@/shared/status-maps';
import { returnStatusTitle } from '@/shared/title-maps';

import { ReturnActions } from './return-actions';
import { ReturnAttachmentsGrid } from './return-attachments-grid';
import { ReturnDetailsSection } from './return-details-section';
import { ReturnLinesCard } from './return-lines-card';
import type { ReturnLine } from './return-lines-card';
import { ReturnOrderCard } from './return-order-card';

const getReturnLineFromDto = (line: ReturnLineDTO): ReturnLine => ({
  id: line.id,
  skuId: line.skuId,
  skuName: line.skuName,
  description: line.description,
  qtyExpected: line.qtyExpected,
  qtyReceived: line.qtyReceived,
});

export function ReturnDetails() {
  const id = useRequiredParam('id');
  const { supplyGateway } = useGateways();

  const {
    data: returnDto,
    isLoading,
    refetch,
  } = useEntityDetailsQuery<ReturnDTO>({
    parentKey: 'returns',
    id,
    query: (fetchId, { signal }) => supplyGateway.getReturn(fetchId, { signal }),
  });

  const lines = useMemo(
    () => (returnDto?.returnLines ?? []).map(getReturnLineFromDto),
    [returnDto?.returnLines],
  );

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/supply/returns" parentTitle="Returns">
          <RouteIcon />
        </HeaderIcon>
        <HeaderTitle pageTitle={returnDto?.name} className="flex flex-wrap items-center gap-2">
          {returnDto?.name}
          {returnDto?.status ? (
            <StatusBadge
              status={returnDto.status}
              type={returnStatusType}
              title={returnStatusTitle}
            />
          ) : null}
        </HeaderTitle>
        <ReturnActions returnDto={returnDto} isLoading={isLoading} onRefresh={refetch} />
      </Header>
      <PageContent variant="split">
        <DetailMain>
          {returnDto?.orderId ? <ReturnOrderCard orderId={returnDto.orderId} /> : null}
          <ReturnLinesCard title="Items" lines={lines} isLoading={isLoading} />
          <ReturnAttachmentsGrid attachments={returnDto?.attachments} />
        </DetailMain>
        <DetailSide>
          <DetailGroup>
            <ReturnDetailsSection returnDto={returnDto} isLoading={isLoading} />
          </DetailGroup>
        </DetailSide>
      </PageContent>
    </Page>
  );
}
